import { VFC, useMemo, useState } from 'react';
import { ComparisonModule } from '../../../lib/api/models/umbraco/content-spot';
import { Button } from '../../shared';

import { CollapsibleCell } from './collapsible-cell';
import {
    BorderBox,
    ColumnHeader,
    ColumnSubHeader,
    ColumnTextWrapper,
    StickyTh,
    StyledIcon,
    Table,
    Tbody,
    Td,
    CtaForTd,
    Th,
    Thead,
    Tr,
    TrWithCta,
    FootnoteText,
    FootNoteWrapper,
    StickyFootNoteTh,
    StickyTr,
    ShowMoreContainer,
    ShowMoreGradient,
} from './comparison-table.styled';
import { CtaHandler } from '../content-handlers';
type Props = {
    tableData: ComparisonModule;
};

const MEDIA_URL = process.env.NEXT_PUBLIC_MEDIA_URL;

export const ComparisonTable: VFC<Props> = ({ tableData }) => {
    const [showMore, setShowMore] = useState(false);

    const tableHasDisclaimerText =
        tableData.columns && tableData.columns.length > 0 && tableData.columns.some((x) => x.footnote && x.footnote.length > 0);

    const tableHasCta = tableData.columns && tableData.columns.length > 0 && tableData.columns.some((x) => x.cta && x.cta.length > 0);

    const shouldShowMore = useMemo(() => {
        return tableData.enableShowMore && !showMore && tableData.rows.length > 10;
    }, [showMore, tableData.enableShowMore, tableData.rows.length]);

    return (
        <BorderBox>
            <Table width={tableData.tableWidth}>
                <Thead>
                    <StickyTr>
                        <StickyTh></StickyTh>
                        {tableData?.columns.map((col, index) => {
                            return (
                                <Th key={`column-${index}`}>
                                    <ColumnTextWrapper>
                                        <ColumnHeader>{col.headerText}</ColumnHeader>
                                        <ColumnSubHeader>{col.subheaderText}</ColumnSubHeader>
                                    </ColumnTextWrapper>
                                </Th>
                            );
                        })}
                    </StickyTr>
                </Thead>
                <Tbody>
                    {tableData.rows?.map((row, index) => {
                        return (
                            <Tr key={`row-${index}`} isHighlighted={index % 2 !== 0} hide={shouldShowMore ? index >= 10 : false}>
                                <StickyTh>
                                    <CollapsibleCell row={row} isAboveFold={!!tableData.isAboveFold} />
                                </StickyTh>
                                {row.cells?.map((cell, cellIndex) => {
                                    return (
                                        <Td key={`cell-${cellIndex}`}>
                                            {cell.icon ? (
                                                <StyledIcon
                                                    src={`${MEDIA_URL}${cell.icon?.src}`}
                                                    alt={row.icon?.name}
                                                    loading={tableData.isAboveFold ? 'eager' : 'lazy'}
                                                />
                                            ) : (
                                                <p>
                                                    <strong>{cell.text}</strong>
                                                </p>
                                            )}
                                        </Td>
                                    );
                                })}
                            </Tr>
                        );
                    })}
                    {shouldShowMore && (
                        <Tr>
                            <Td colSpan={tableData.columns.length + 1}>
                                <ShowMoreContainer>
                                    <ShowMoreGradient></ShowMoreGradient>
                                    <Button alignSelf="left" variant="primary" onClick={() => setShowMore(true)}>
                                        Indlæs flere
                                    </Button>
                                </ShowMoreContainer>
                            </Td>
                        </Tr>
                    )}

                    {(tableHasCta || tableHasDisclaimerText) && (
                        <TrWithCta>
                            {tableHasDisclaimerText || tableHasCta ? (
                                <StickyFootNoteTh>
                                    <FootNoteWrapper>
                                        {tableData.columns.map((col, index) => {
                                            return <FootnoteText key={`col-footnote-${index}`}>{col.footnote}</FootnoteText>;
                                        })}
                                    </FootNoteWrapper>
                                </StickyFootNoteTh>
                            ) : null}

                            {!shouldShowMore && tableHasCta
                                ? tableData.columns.map((col, index) => {
                                      return (
                                          <td key={`cta-cell-${index}`}>
                                              {col.cta && col.cta.length > 0 ? (
                                                  <CtaForTd>
                                                      <CtaHandler ctas={col.cta} unsetMinWidth={true}></CtaHandler>
                                                  </CtaForTd>
                                              ) : null}
                                          </td>
                                      );
                                  })
                                : null}
                        </TrWithCta>
                    )}
                </Tbody>
            </Table>
        </BorderBox>
    );
};
